import { skillsApiUrl, skillsAuthHeaders } from "@/services/skills";
import { detailFromBody, friendlyHttpMessage, notifyBudgetExceeded } from "@/lib/http/apiError";

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

function buildUrl(path: string): string {
  return skillsApiUrl(path);
}

async function parseError(res: Response): Promise<ApiError> {
  let body: unknown = null;
  let message = res.statusText;
  try {
    const ctype = res.headers.get("content-type") ?? "";
    if (ctype.includes("application/json")) {
      body = await res.json();
      // Was: `message = body.detail` verbatim. That surfaced the backend's raw code —
      // an over-budget seat read "llm_budget_exceeded" in the toast. `friendlyHttpMessage`
      // still passes an unrecognised code through unchanged, so nothing else regresses.
      message = friendlyHttpMessage({ status: res.status, body });
      notifyBudgetExceeded(detailFromBody(body));
    } else {
      const text = await res.text();
      body = text;
      if (text) message = text;
    }
  } catch {
    /* ignore */
  }
  return new ApiError(res.status, message || "request_failed", body);
}

export async function apiGet<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(buildUrl(path), {
    method: "GET",
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...skillsAuthHeaders(),
      ...(init?.headers as Record<string, string> | undefined),
    },
    ...init,
  });
  if (!res.ok) throw await parseError(res);
  const ctype = res.headers.get("content-type") ?? "";
  if (ctype.includes("application/json")) {
    return unwrapApiEnvelope<T>(await res.json());
  }
  return (await res.text()) as unknown as T;
}

export async function apiPost<T>(
  path: string,
  body: unknown,
  init?: RequestInit
): Promise<T> {
  const res = await fetch(buildUrl(path), {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...skillsAuthHeaders(),
      ...(init?.headers as Record<string, string> | undefined),
    },
    body: JSON.stringify(body ?? {}),
    ...init,
  });
  if (!res.ok) throw await parseError(res);
  return unwrapApiEnvelope<T>(await res.json());
}

export async function apiPut<T>(
  path: string,
  body: unknown,
  init?: RequestInit
): Promise<T> {
  const res = await fetch(buildUrl(path), {
    method: "PUT",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...skillsAuthHeaders(),
      ...(init?.headers as Record<string, string> | undefined),
    },
    body: JSON.stringify(body ?? {}),
    ...init,
  });
  if (!res.ok) throw await parseError(res);
  return unwrapApiEnvelope<T>(await res.json());
}

export async function apiDelete(path: string, init?: RequestInit): Promise<void> {
  const res = await fetch(buildUrl(path), {
    method: "DELETE",
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...skillsAuthHeaders(),
      ...(init?.headers as Record<string, string> | undefined),
    },
    ...init,
  });
  if (!res.ok && res.status !== 204) throw await parseError(res);
}

export async function apiBlob(path: string, init?: RequestInit): Promise<{ blob: Blob; filename?: string }> {
  const res = await fetch(buildUrl(path), {
    method: "GET",
    credentials: "include",
    headers: {
      ...skillsAuthHeaders(),
      ...(init?.headers as Record<string, string> | undefined),
    },
    ...init,
  });
  if (!res.ok) throw await parseError(res);
  const disposition = res.headers.get("content-disposition") ?? "";
  const filename = filenameFromDisposition(disposition);
  return { blob: await res.blob(), filename };
}

export function apiUrl(path: string): string {
  return buildUrl(path);
}

export function getAuthHeader(): Record<string, string> {
  return skillsAuthHeaders();
}

function unwrapApiEnvelope<T>(payload: unknown): T {
  if (
    payload &&
    typeof payload === "object" &&
    "data" in payload &&
    ("status" in payload || "statusCode" in payload || "message" in payload)
  ) {
    return (payload as { data: T }).data;
  }
  return payload as T;
}

function filenameFromDisposition(disposition: string): string | undefined {
  const utf8 = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (utf8) {
    try {
      return decodeURIComponent(utf8);
    } catch {
      return utf8;
    }
  }
  return disposition.match(/filename="([^"]+)"/i)?.[1];
}
